import { Icon, ItemStatus, StatusProxy } from "@focus/focusUI";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { type IPlotItem } from "../../containers/PlotsContainer";
import { useHashParams } from "../../hooks/useHashParams";
import {
  useConjunctionCDM,
  useConjunctionDetail,
} from "../../services/conjunctions";
import { useConfig } from "../../services/helpers/hooks";
import { useSystemConfig } from "../../hooks/useSystemConfig";
import { ContentHeader } from "../ContentHeader";
import { Conjunction3DPanel } from "./Conjunction3DPanel";
import { ConjunctionInformationDetails } from "./ConjunctionInformationDetails";
import { ConjunctionPlotsPanel } from "./ConjunctionPlotsPanel";
import { getNestedValue } from "./conjunctionUiUtils";

interface ConjunctionInformationProps {
  pageTitle?: string;
}

export const ConjunctionInformation: React.FC<ConjunctionInformationProps> = ({
  pageTitle = "Conjunctions",
}) => {
  const navigate = useNavigate();
  const { getParam, setParam } = useHashParams();
  const conjunctionConfig = useConfig("conjunctions");
  const { currentConfig } = useSystemConfig();
  const {
    value: conjunction,
    status,
    error,
  } = useConjunctionDetail(conjunctionConfig);

  const plots: IPlotItem[] = useMemo(() => {
    const conjPlots = conjunction?.plots || [];
    const eventPlots = conjunction?.event?.plots || [];
    return [...conjPlots, ...eventPlots];
  }, [conjunction]);

  const fetchedCDM = useConjunctionCDM(conjunctionConfig.id);
  const cdm = fetchedCDM.value;

  const tabConfig = [
    {
      id: "Details",
      title: "Details",
      show: true,
      render: () => (
        <ConjunctionInformationDetails
          conjunction={conjunction}
          riskConfig={currentConfig}
        />
      ),
    },
    {
      id: "Plots",
      title: "Plots",
      show: Boolean(plots.length),
      render: () => (
        <ConjunctionPlotsPanel
          conjunction={conjunction}
          plots={plots}
          riskConfig={currentConfig}
        />
      ),
    },
    {
      id: "3d-view",
      title: "3D View",
      show: true,
      render: () => (
        <Conjunction3DPanel
          cdm={cdm}
          conjunction={conjunction}
          riskConfig={currentConfig}
        />
      ),
    },
  ];

  const eventIdValue =
    getNestedValue(conjunction, ["event", "id"]) ??
    getNestedValue(conjunction, ["event", "eventId"]);
  const hasEventId =
    eventIdValue !== undefined && eventIdValue !== null && eventIdValue !== "-";

  const getPageTitle = () => (
    <div className="flex min-w-0 justify-between">
      <button
        className="-ml-1 inline-flex w-fit cursor-pointer appearance-none items-center gap-1 rounded-md border-0 bg-transparent px-1.5 py-1 text-sm font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
        onClick={() => navigate(-1)}
        type="button"
      >
        <Icon icon="chevron-left" size={14} />
        Back to {pageTitle}
      </button>
    </div>
  );

  const visibleTabs = tabConfig.filter((t) => t.show);

  const [selectedTabId, setSelectedTabId] = React.useState<string | undefined>(
    getParam("tab"),
  );

  React.useEffect(() => {
    if (status === ItemStatus.PENDING) return;

    if (!visibleTabs.length) {
      navigate(-1);
      return;
    }

    const tabFromHash = getParam("tab");
    const tabExists = visibleTabs.some((t) => t.id === tabFromHash);

    if (tabFromHash && tabExists) {
      setSelectedTabId(tabFromHash);
      return;
    }

    // Tab not valid or do not exist --> fallback a to first visible
    const defaultTab = visibleTabs[0].id;
    setSelectedTabId(defaultTab);
    setParam("tab", defaultTab);
  }, [status, visibleTabs, getParam, setParam, navigate]);

  const handleSelectTab = (tab: string) => {
    setSelectedTabId(tab);
    setParam("tab", tab);
  };

  return (
    <StatusProxy status={status} error={error}>
      <div className="foc-content flex flex-col overflow-hidden">
        <ContentHeader
          renderCustomTitle={getPageTitle}
          tabs={visibleTabs}
          tabsActions={
            hasEventId ? (
              <button
                className="focusoc-theme-primary inline-flex min-h-10 items-center rounded-lg border px-4 text-sm font-bold shadow-sm transition"
                onClick={() => navigate(`/event/${String(eventIdValue)}/info`)}
                type="button"
              >
                Go to event
              </button>
            ) : null
          }
          selectedTab={selectedTabId}
          handleSelectTab={handleSelectTab}
        />
        <div className="foc-table-container flex min-h-0 flex-1 overflow-hidden">
          {visibleTabs.map((tab) =>
            selectedTabId === tab.id ? (
              <React.Fragment key={tab.id}>{tab.render()}</React.Fragment>
            ) : null,
          )}
        </div>
      </div>
    </StatusProxy>
  );
};
